import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { Maximize2, RotateCcw } from "lucide-react";
import type { EventMarker, IndicatorSeries, RangeKey, ViewMode } from "../types";
import { filterByRange, mergeSeries, transformSeries, unitLabel } from "../utils/series";

interface Props {
  title: string;
  subtitle: string;
  series: IndicatorSeries[];
  range?: RangeKey;
  viewMode?: ViewMode;
  referenceZero?: boolean;
  height?: number;
  events?: EventMarker[];
  syncId?: string;
  onOpenDetail?: () => void;
  showOpenButton?: boolean;
  interactiveZoom?: boolean;
}

const COLORS = ["#2563eb", "#dc2626", "#7c3aed", "#059669", "#d97706", "#0891b2"];

function activeLabel(state: unknown): string | null {
  if (!state || typeof state !== "object") return null;
  const candidate = (state as { activeLabel?: unknown }).activeLabel;
  return typeof candidate === "string" ? candidate : null;
}

function labelledEventIds(events: EventMarker[], maxLabels = 3) {
  if (events.length <= maxLabels) return new Set(events.map((e) => e.id));
  const picked = new Set<string>();
  for (let i = 0; i < maxLabels; i++) {
    const index = Math.round((i * (events.length - 1)) / Math.max(1, maxLabels - 1));
    picked.add(events[index].id);
  }
  return picked;
}

export default function TimeSeriesChart({
  title,
  subtitle,
  series,
  range = "MAX",
  viewMode = "level",
  referenceZero = false,
  height = 290,
  events = [],
  syncId = "dashboard",
  onOpenDetail,
  showOpenButton = true,
  interactiveZoom = false
}: Props) {
  const renderedSeries = useMemo(
    () => series.map((s) => filterByRange(transformSeries(s, viewMode), range)),
    [series, range, viewMode]
  );
  const data = useMemo(() => mergeSeries(renderedSeries), [renderedSeries]);
  const unitGroups = useMemo(() => Array.from(new Set(renderedSeries.map((s) => unitLabel(s.unit)))), [renderedSeries]);
  const leftUnit = unitGroups[0] ?? "";
  const rightUnits = unitGroups.slice(1);
  const hasRightAxis = rightUnits.length > 0;
  const axisFor = (s: IndicatorSeries) => unitLabel(s.unit) === leftUnit ? "left" : "right";
  const [zoom, setZoom] = useState<{ start: string; end: string } | null>(null);
  const [dragStart, setDragStart] = useState<string | null>(null);
  const [dragEnd, setDragEnd] = useState<string | null>(null);

  useEffect(() => {
    setZoom(null);
    setDragStart(null);
    setDragEnd(null);
  }, [range, viewMode, series]);

  const chartData = useMemo(() => {
    if (!zoom) return data;
    return data.filter((row) => {
      const date = String(row.date);
      return date >= zoom.start && date <= zoom.end;
    });
  }, [data, zoom]);

  const demo = series.some((s) => s.source.quality === "demo");
  const unavailable = series.every((s) => s.source.quality === "unavailable" || s.observations.length === 0);
  const stale = !unavailable && series.some((s) => s.source.quality === "stale");
  const hasData = chartData.some((row) => renderedSeries.some((item) => typeof row[item.id] === "number"));
  const visibleDates = new Set(chartData.map((d) => String(d.date)));
  const filteredEvents = events.filter((e) => visibleDates.has(e.date));
  const eventLabels = labelledEventIds(filteredEvents);

  const formatDate = (date: string) => {
    const [y, m, d] = date.split("-");
    return d && d !== "01" ? `${y}.${m}.${d}` : `${y}.${m}`;
  };

  const finishZoom = (state: unknown) => {
    if (!interactiveZoom || !dragStart) return;
    const end = activeLabel(state) ?? dragEnd;
    if (end && end !== dragStart) {
      const [startDate, endDate] = dragStart < end ? [dragStart, end] : [end, dragStart];
      setZoom({ start: startDate, end: endDate });
    }
    setDragStart(null);
    setDragEnd(null);
  };

  return (
    <section className="chart-card">
      <header className="chart-card__header">
        <div>
          <div className="chart-card__title-row">
            <h2>{title}</h2>
            {demo && <span className="demo-pill">데모</span>}
            {unavailable && <span className="quality-pill quality-pill--na">N/A</span>}
            {stale && <span className="quality-pill quality-pill--stale">지연</span>}
          </div>
          <p>{subtitle}{interactiveZoom && hasData ? " · 드래그로 구간 확대" : ""}</p>
        </div>
        <div className="chart-card__actions">
          {interactiveZoom && zoom && (
            <button className="icon-button" onClick={() => setZoom(null)} aria-label={`${title} 확대 초기화`} title="확대 초기화">
              <RotateCcw size={15} />
            </button>
          )}
          {showOpenButton && onOpenDetail && (
            <button className="icon-button" onClick={onOpenDetail} aria-label={`${title} 상세보기`} title="상세보기">
              <Maximize2 size={16} />
            </button>
          )}
        </div>
      </header>

      <div className={`chart-plot ${interactiveZoom ? "chart-plot--zoomable" : ""}`} style={{ width: "100%", height }}>
        {!hasData && <div className="chart-empty">공식 원자료가 아직 연결되지 않았거나 검증에 실패했습니다.</div>}
        <ResponsiveContainer>
          <LineChart
            data={chartData}
            margin={{ top: 12, right: 22, left: 4, bottom: 2 }}
            syncId={syncId}
            syncMethod="value"
            onMouseDown={(state: unknown) => {
              if (!interactiveZoom) return;
              const label = activeLabel(state);
              if (label) { setDragStart(label); setDragEnd(label); }
            }}
            onMouseMove={(state: unknown) => {
              if (!interactiveZoom || !dragStart) return;
              const label = activeLabel(state);
              if (label) setDragEnd(label);
            }}
            onMouseUp={finishZoom}
            onMouseLeave={() => {
              if (dragStart) { setDragStart(null); setDragEnd(null); }
            }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.18} />
            <XAxis
              dataKey="date"
              minTickGap={28}
              tickFormatter={formatDate}
              tick={{ fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              tick={{ fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={52}
              domain={["auto", "auto"]}
            />
            {hasRightAxis && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={58}
                domain={["auto", "auto"]}
              />
            )}
            <Tooltip
              labelFormatter={(v: unknown) => formatDate(String(v))}
              contentStyle={{
                borderRadius: 12,
                border: "1px solid rgba(148,163,184,.35)",
                boxShadow: "0 10px 30px rgba(15,23,42,.12)",
                maxWidth: 320,
                whiteSpace: "normal",
                overflowWrap: "anywhere"
              }}
            />
            {referenceZero && <ReferenceLine yAxisId="left" y={0} stroke="#64748b" strokeDasharray="4 4" />}
            {filteredEvents.map((e) => (
              <ReferenceLine
                key={e.id}
                yAxisId="left"
                x={e.date}
                stroke="#94a3b8"
                strokeDasharray="3 4"
                label={eventLabels.has(e.id) ? { value: e.quality === "demo" ? "데모" : e.label, position: "insideTopRight", fontSize: 9 } : undefined}
              />
            ))}
            {dragStart && dragEnd && dragStart !== dragEnd && (
              <ReferenceArea yAxisId="left" x1={dragStart} x2={dragEnd} strokeOpacity={0.25} fillOpacity={0.08} />
            )}
            {renderedSeries.map((s, idx) => (
              <Line
                key={s.id}
                type="monotone"
                dataKey={s.id}
                yAxisId={axisFor(s)}
                name={`${s.shortName} (${unitLabel(s.unit)})`}
                stroke={COLORS[idx % COLORS.length]}
                strokeWidth={2.1}
                dot={false}
                connectNulls={false}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-legend" aria-label={`${title} 범례`}>
        {renderedSeries.map((s, idx) => (
          <span className="chart-legend__item" key={s.id} title={s.name}>
            <i style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
            <b>{s.shortName}</b>
            <small>{unitLabel(s.unit)}</small>
          </span>
        ))}
      </div>

      <footer className="chart-card__footer">
        {hasRightAxis && <span>좌축 {leftUnit} · 우축 {rightUnits.join(" / ")}</span>}
        {filteredEvents.length > 3 && <span>이벤트선 {filteredEvents.length}개 · 라벨은 겹침 방지를 위해 일부만 표시</span>}
        {series.map((s) => (
          <span key={s.id}>
            {s.shortName}: {s.source.provider} · {s.source.frequency}
          </span>
        ))}
      </footer>
    </section>
  );
}
